import { Input, SegmentedControl, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconFunctionFilled } from '@tabler/icons-react';
import { forwardRef, useEffect, useState } from 'react';
import type { ExecutorVar } from '~/common/vm/protocol';
import { MonacoInputArea } from '~/components/forms/input/MonacoInputArea';
import type { ValueType } from '~/server/logic/steps/operation/types';

export const ParameterizedTextInputArea = forwardRef<
  HTMLTextAreaElement | HTMLDivElement,
  {
    rows?: number;
    placeholder?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    className?: string;
    label?: string;
    readonlyMessage?: string;
    value?: string;
    valueType: ValueType;
    // Make sure the parent memozies this!
    onChange?: (value: string, valueType: ValueType) => void;
    variables?: ExecutorVar[];
  }
>(
  (
    {
      autoFocus,
      disabled,
      rows,
      className,
      placeholder,
      label,
      readonlyMessage,
      value,
      valueType,
      onChange,
      variables,
    },
    ref,
  ) => {
    const [inputType, setInputType] = useState<ValueType>(valueType);
    const [inputTypeWasSet, setInputTypeWasSet] = useState<boolean>(false);

    const form = useForm({
      initialValues: {
        staticValue: valueType === 'STATIC' ? value || '' : '',
        // TODO: deal with non-strings values in the future
        // We can give a nice default for dynamic values, not so much for static ones.
        dynamicValue:
          valueType === 'DYNAMIC' ? value || ''
          : value ? `\`${value}\``
          : '``',
      },
    });

    useEffect(() => {
      onChange?.(inputType === 'STATIC' ? form.values.staticValue : form.values.dynamicValue, inputType);
    }, [form.values.staticValue, form.values.dynamicValue, inputType]); // don't add onChange to the dependency array, infinite rerender

    return (
      <Input.Wrapper
        className={className}
        label={
          <div className="flex items-baseline justify-between">
            <span>{label}</span>
            <SegmentedControl
              size="xs"
              defaultValue={valueType}
              onChange={(value) => {
                setInputType(value as ValueType);
                setInputTypeWasSet(true);
              }}
              data={[
                {
                  value: 'STATIC' satisfies ValueType,
                  label: 'Static',
                },
                {
                  value: 'DYNAMIC' satisfies ValueType,
                  label: (
                    <IconFunctionFilled
                      title="Dynamic"
                      size={19}
                    />
                  ),
                },
              ]}
            />
          </div>
        }
        labelProps={{ className: 'w-full' }}
      >
        {inputType === 'STATIC' ?
          <Textarea
            // We don't want a gap between the label and the input
            classNames={{
              root: 'pt-0 mt-0',
            }}
            placeholder={placeholder}
            rows={rows}
            key="static"
            autoFocus={inputTypeWasSet || autoFocus}
            disabled={disabled}
            // Disable inner wrapping of the input
            inputWrapperOrder={['input']}
            ref={ref as React.ForwardedRef<HTMLTextAreaElement>}
            {...form.getInputProps('staticValue')}
          />
        : <MonacoInputArea
            key="dynamic"
            rows={rows}
            readonlyMessage={readonlyMessage}
            autoFocus={inputTypeWasSet || autoFocus}
            disabled={disabled}
            // Disable inner wrapping of the input
            inputWrapperOrder={['input']}
            variables={variables}
            ref={ref as React.ForwardedRef<HTMLDivElement>}
            {...form.getInputProps('dynamicValue')}
          />
        }
      </Input.Wrapper>
    );
  },
);

ParameterizedTextInputArea.displayName = 'ParameterizedTextInputArea';
